'use client'

import React, {
	type ReactNode,
	createContext,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react'

import { useStoredPreviousUrl } from '../../hooks/useStoredPreviousUrl'
import type { Module } from '../../types/modules'
import type { ProductInfo } from '../../types/product'
import { getLastVisitedProductModuleIndex } from '../../utils/module'
import { getProductInfoFromPreviousUrl } from '../../utils/product'

interface InspirationalProviderProps {
	readonly modules: Module[]
	readonly defaultVisibleModules: number
	readonly children: ReactNode
}

type ReferrerInfo = {
	pathname: string
	product: ProductInfo | null
}

export interface InspirationalContextType {
	nVisibleModules: number
	allModulesAreVisible: boolean
	referrerInfo: ReferrerInfo
	incVisibleModules: (inc: number) => void
}

export const InspirationalContext = createContext<InspirationalContextType>({
	nVisibleModules: 0,
	allModulesAreVisible: false,
	referrerInfo: {
		pathname: '',
		product: null,
	},
	incVisibleModules: () => {},
})

export const InspirationalProvider = ({
	modules,
	defaultVisibleModules,
	children,
}: InspirationalProviderProps) => {
	const storedPreviousUrl = useStoredPreviousUrl()
	const lastVisitedProductInfo = useMemo(
		() => getProductInfoFromPreviousUrl(storedPreviousUrl),
		[storedPreviousUrl]
	)

	const [nVisibleModules, setNVisibleModules] = useState(defaultVisibleModules)

	// Computed initial value for visible modules should be an effect to avoid hydratation issues
	useEffect(() => {
		if (lastVisitedProductInfo) {
			const lastProductModuleIndex = getLastVisitedProductModuleIndex(
				modules,
				lastVisitedProductInfo
			)
			if (lastProductModuleIndex >= defaultVisibleModules) {
				setNVisibleModules(lastProductModuleIndex + 1)
			}
		}
	}, [defaultVisibleModules, lastVisitedProductInfo, modules])

	const incVisibleModules = useCallback(
		(inc: number) => {
			setNVisibleModules((currentVisibleModules) => {
				const newVisibleModules = currentVisibleModules + inc
				if (newVisibleModules <= modules.length) {
					return newVisibleModules
				}
				return modules.length
			})
		},
		[modules.length]
	)

	const [previousPathname] = storedPreviousUrl.split('?')

	const referrerInfo = useMemo(
		() => ({
			pathname: previousPathname,
			product: lastVisitedProductInfo,
		}),
		[lastVisitedProductInfo, previousPathname]
	)

	const allModulesAreVisible = modules.length === nVisibleModules

	useLayoutEffect(() => {
		/**
		 * If we are on a persisted page, we can take advantage of native scroll
		 * restoration as we have all products initially available, otherwise we
		 * have to manually restore the scroll position
		 */
		if ('scrollRestoration' in history) {
			history.scrollRestoration = 'manual'
			window.onpageshow = (event) => {
				if (event.persisted) {
					window.history.scrollRestoration = 'auto'
				}
			}
		}
	}, [])

	const contextState = useMemo<InspirationalContextType>(
		() => ({
			nVisibleModules,
			allModulesAreVisible,
			referrerInfo,
			incVisibleModules,
		}),
		[allModulesAreVisible, incVisibleModules, nVisibleModules, referrerInfo]
	)

	return (
		<InspirationalContext.Provider value={contextState}>
			{children}
		</InspirationalContext.Provider>
	)
}
